import React, { useEffect, useState } from "react";
import { UnlinkIcon } from "assets/svg";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Button, Input, Modal, MultiSelect, Text, Option, Icon, Radio, ConfirmationDialog } from "src/components";
import { theme } from "src/config";
import { Space } from "src/models";
import { getRoutePath, Page } from "src/routes";
import { RoutingStore, SpaceStore, AuthStore } from "src/stores";
import styled from "styled-components";
import { Card } from "./card";
import { ProjectTheme, SpaceType } from "src/types";
import Box from "react-raster";
import toast from "react-hot-toast";
import { useClickOutside } from "src/util/util";

export interface IRoomCardProps {
  data:          Space;
  spaceStore?:   SpaceStore;
  authStore?:    AuthStore;
  routingStore?: RoutingStore;
  onClick?():  void;
  onUnlink?(): void;
}

export const RoomCard = inject("spaceStore", "routingStore", "authStore")(observer((props: IRoomCardProps): JSX.Element => {
  const { data: room, spaceStore, routingStore, authStore } = props;

  const cardRef = React.useRef<HTMLDivElement>(null);

  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [showValueError,    setShowValueError]    = React.useState(false);
  const [value, setValue] = React.useState("");
  const themes: Option[] = Object.values(ProjectTheme).map((t) => ({
    value: t,
    label: t
  }));
  const { t, i18n } = useTranslation();
  const { getCurUser: currentUser } = authStore;

  const handleValueChange = (value: string): void => {
    setValue(value);
  };

  const enterButtonAction = (): void => {
    if(!authStore.getCurUser.inSpace(room.id)) return;
    spaceStore.changeRoom(room.id, true);
  };

  const handleValueValidation = (): void => {
      const validValue = room.title;
    if (validValue === value) {
      setShowValueError(false);
    } else {
      setShowValueError(true);
    }
  };

  useEffect(() => {
    setShowValueError(false);
  }, [value]);

  const parent:     Space   = room.parent && spaceStore.getItemByID(room.parent); // Could be project or room
  const childRooms: Space[] = spaceStore.getItemsByParent(room.id, SpaceType.Room);
  const projRooms:  Space[] = spaceStore.getItemsByParent(authStore.getCurProjectID, SpaceType.Room).filter(r => r.id !== room.id);

  const isSubRoom: boolean = parent.type === SpaceType.Room;

  useClickOutside(cardRef, () => {
    if(spaceStore.getActive && room.id === spaceStore.getActive.id && !showConfirmDialog) {
      spaceStore.setActive(null, true);
    }
  });

  return (
    <Card
      ref      = {cardRef}
      stack    = {Boolean(childRooms.length)}
      key      = {room.id}
      editable = {currentUser.canEdit}
      expanded = {Boolean(spaceStore.getActive && room.id === spaceStore.getActive.id)}
      onClick  = {props.onClick || ((e) => spaceStore.setActive(room))}
      height   = "auto"
      width    = "24rem"
      body     = {
        <>
        <Text size="default">{room.title}</Text>
        <CardBodyContainer>
        {authStore.getCurUser.getRole(room.id) && authStore.getCurUser.canEditSpace(room.id) && (
          <StyledButton variant='outlined' size='small' onClick={() => enterButtonAction()}>{t('enter')}</StyledButton>
        )}
        </CardBodyContainer>
      </>
    }        
      expandedBody = { 
        <CardContainer>
          <RoomContainerHead>
            <>
            <Input
              editOnFocus
              autoFocus
              value    = {room.title}
              onBlur   = {() => room.save({title: room.title})}
              label    = {t("room_name")}
              onChange = {(val) => {
                room.set("title", val);
              }}
            />
            {!isSubRoom && 
              <MultiSelect
                disableAutoFocus
                label       = {t("sub_room_plural")}
                placeholder = {t("sub_room_plural")}
                selected    = {(childRooms || []).map(r => ({ value: r, label: r.title }))}
                onAdd       = {(sel: Option) => {
                  (sel.value as Space).set("parent", room.id, true);
                }}
                onRemove      = {(sel: Option) => {
                  (sel.value as Space).set("parent", room.parent, true);
                }}
                options = {[...projRooms, ...childRooms].map(r => ({ value: r, label: r.title }))}
              />
            }
            </>
          </RoomContainerHead>
          <RoomCardContent>
          {/*  <MultiSelect
                label    = {t('theme')}
                options  = {themes}
                selected = {[{ value: room.theme, label: room.theme }]}
                onChange = {(item: Option) => {
                  room.set("theme", item.value, true);
                }}
                disableSearch
                singleSelect
                forceSelect
              /> */}
              <Text size="small">{t("member_listing_display")}</Text>
            <RadioContainer>
              <Radio
                checked = {room.member_listing === "name"}
                value   = {t("display_by_name")}
                onClick = {() => room.set("member_listing", "name", true)}
              />
              <Radio
                checked = {room.member_listing === "title"}
                value   = {t("display_by_title")}
                onClick = {() => room.set("member_listing", "title", true)}
              />
            </RadioContainer>
          </RoomCardContent>
          <ButtonContainer>
            <Modal show={showConfirmDialog}>
              <ConfirmationDialog
                onConfirm = {async () => {
                  try {
                    await room.remove();
                    spaceStore.setActive(null);
                    toast.success(t("remove_room"), {
                      icon: <Icon size="medium" iconId="delete"/>
                    });;
                  } catch (err) {
                    console.error("Failed to remove room", room);
                  }
                }}
                onCancel  = {() => setShowConfirmDialog(false)}
                label     = {t("confirm_action")}
                message   = {t("confirm_action_delete_space_message", {space: room.title, type: t("meetingroom")})}
                disabled  = {value !== room.title}
                content   = {<Input value={value} showError={showValueError} onBlur={handleValueValidation} 
                onChange={(value) => handleValueChange(value)} type="text" errorMessage={t("invalid_space_name", {type: t("room")})}/>}
              />
            </Modal>
            <Button variant="icon" onClick={() => setShowConfirmDialog(true)}>
              <Icon iconId="delete" size="large" color={theme.color.grayDark} />
            </Button>
            {props.onUnlink && 
            <Button variant="icon" onClick={() => props.onUnlink()}>
              <UnlinkIcon color={theme.color.grayDark}/>
            </Button>
          }
          </ButtonContainer>
        </CardContainer>
      }
      footer={
        <Box>
              {authStore.getCurUser.getRole(room.id) && (
            <Text size="small">{`${t("my_role")}: ${t(authStore.getCurUser.getRole(room.id))}`}</Text>
          )}
          {!authStore.getCurUser.getRole(room.id) && (
            <Text size="small">{`${t("your_not_in_this_room")}`}</Text>
          )}
        </Box>
      }
    />
  );
}));

const RoomContainerHead = styled.div`
  flex-direction: column;
  display:        flex;
  gap:            1.5rem;
`;

const RadioContainer = styled.div`
  display:        flex;
  flex-direction: column;
`;

const RoomCardContent = styled.div`
  display:        grid;
  flex-direction: column;
  gap:            1rem;
  margin:         1rem 0rem;
`;

const CardBodyContainer = styled.div`
  display:         flex;
  flex-direction:  column;
  justify-content: center;
  align-items:     center;
`

const ButtonContainer = styled.div`
  display:         flex;
  width:           100%;
  justify-content: space-between;
  flex-direction:  row-reverse;
`;

const CardContainer = styled.div`
  display:        flex;
  flex-direction: column;
  gap:            1rem;
`

const StyledButton = styled(Button)`
  padding-left:  3rem;
  padding-right: 3rem;
`

const ActionItem = styled.div`
  :hover {
    cursor: pointer;
    svg {
      transform: rotate(40deg);
    }
  }
`;
